import { Card, Typography, Input, DatePicker, message, Button, Select } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment"; // Ensure moment is imported
import { getData, deleteDoc } from "../../utils";
import { DeleteFilled, RedoOutlined } from "@ant-design/icons";
import ConfirmationModal from "../../Modal/ConfirmationModal";
import { useLoaderProvider } from "../../context/LoaderProvider";

const ManageDoctor = () => {
  const { RangePicker } = DatePicker;
  const { Search } = Input;
  const [cnfrm, setConfirm] = useState(false);
  const [docId, setDocId] = useState(null);
  const { updateLoader } = useLoaderProvider();
  const [messageApi, contextHolder] = message.useMessage();

  // State for pagination, search parameters, and doctors data
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [regDateFrom, setRegDateFrom] = useState(null);
  const [regDateTo, setRegDateTo] = useState(null);
  const [doctor, setDoctor] = useState("");
  const [doctorsData, setDoctorsData] = useState([]); // State for storing doctors data
  const [error, setError] = useState(null); // State for error handling
  const [planType, setPlanType] = useState("");

  const location = useLocation();
  const navigate = useNavigate(); // Use useNavigate to manipulate the history

  // Function to handle search
  const onSearch = (value) => {
    updateQueryParams({ doctor: value });
  };

  // Update query parameters based on new state, ensuring no undefined or empty values are passed
  const updateQueryParams = (newParams) => {
    const queryParams = new URLSearchParams(location.search);

    Object.keys(newParams).forEach((key) => {
      if (newParams[key] !== null && newParams[key] !== "") {
        queryParams.set(key, newParams[key]);
      } else {
        queryParams.delete(key); // Remove the parameter if the value is null or empty
      }
    });

    navigate({ search: queryParams.toString() }); // Navigate to the new URL with updated search params
  };

  // Clear all filters
  const clearFilters = () => {
    setRegDateFrom(null);
    setRegDateTo(null);
    setDoctor("");
    setPlanType("");
    navigate({ search: "" }); // Clear all query parameters
    window.location.reload();
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    
    const queryPage = queryParams.get("page");
    const queryLimit = queryParams.get("limit");
    const queryRegDateFrom = queryParams.get("regDateFrom");
    const queryRegDateTo = queryParams.get("regDateTo");
    const queryDoctor = queryParams.get("doctor");
    const queryPlanType = queryParams.get("planType");

    setPage(queryPage ? parseInt(queryPage) : 1);
    setLimit(queryLimit ? parseInt(queryLimit) : 10);
    setRegDateFrom(queryRegDateFrom ? moment(queryRegDateFrom) : null);
    setRegDateTo(queryRegDateTo ? moment(queryRegDateTo) : null);
    setDoctor(queryDoctor || "");
    setPlanType(queryPlanType || "");

    // Fetch doctors whenever the location changes
    fetchDoctors();
  }, [location.search]);

  const fetchDoctors = async () => {
    try {
      const queryParams = new URLSearchParams(location.search);
      const apiParams = {
        page: queryParams.get("page"),
        limit: queryParams.get("limit"),
        regDateFrom: queryParams.get("regDateFrom"),
        regDateTo: queryParams.get("regDateTo"),
        doctor: queryParams.get("doctor"),
        planType: queryParams.get("planType"),
      };

      // Remove undefined or null values
      const filteredParams = Object.entries(apiParams)
        .filter(([, value]) => value !== undefined && value !== null)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");

      const apiUrl = `/v1/admin/doctors${filteredParams ? `?${filteredParams}` : ""}`;
      const res = await getData(apiUrl);

      setDoctorsData(res.data);
      setError(null); // Clear any previous error
    } catch (err) {
      setError(err.message || "An error occurred while fetching data.");
      message.error(err.message || "An error occurred while fetching data.");
    }
  };

  // Handle date range selection
  const handleDateChange = (dates) => {
    const [from, to] = dates || [null, null];
    setRegDateFrom(from ? moment(from) : null);
    setRegDateTo(to ? moment(to) : null);

    updateQueryParams({
      regDateFrom: from ? from.format("YYYY-MM-DD") : null,
      regDateTo: to ? to.format("YYYY-MM-DD") : null,
    });
  };

  

  const deleteUser = async () => {
    updateLoader(true);
    setConfirm(false);
    const res = await deleteDoc(`/v1/admin/doctors/${docId}`);
    updateLoader(false);
    setDocId(null);
    if (res.status === 200) {
      await fetchDoctors();
    }
  };

  const changeValue = (value) => {
    const formatMapping = {
      student: "Student",
      alliedHealth: "Alid Health",
      "nurse/midwife": "Nurse/Midwife",
      doctor: "Doctor",
      nurse: "Nurse",
      other: "Other",
    };
    return formatMapping[value] || value;
  };

  return (
    <>
      <ConfirmationModal
        isOpen={cnfrm}
        ConfirmationParagraph={"Are you sure?"}
        onCancel={() => {
          setConfirm(false);
          setDocId(null);
        }}
        onOk={deleteUser}
      />
      <Card>
        <div className="flex justify-between items-center mb-4 gap-3">
          <Typography className="text-xl font-semibold" align={"left"}>
            Doctors
          </Typography>
          <div className="flex justify-end gap-3">
            <div className="flex items-center">
              <span className="font-semibold mr-2">Registered Date :</span>

              <RangePicker
                //value={[regDateFrom, regDateTo]} // Moment objects for RangePicker
                onChange={handleDateChange}
                format="YYYY-MM-DD" // Ensure the format is correct
                mode={["date", "date"]}
              />
            </div>
            <Select
              placeholder="Select subscription plan"
              style={{
                width: 200,
              }}
              options={[
                {
                  value: "lite",
                  label: "Lite",
                },
                {
                  value: "pro",
                  label: "Pro",
                },
                {
                  value: "institute",
                  label: "Institution",
                },
              ]}
              onChange={(e) => {
                setPlanType(e);
                updateQueryParams({
                  planType: e,
                });
              }}
            />
            <Search
              placeholder="Search by Doctor Name"
              onSearch={onSearch}
              onChange={(e) => setDoctor(e.target.value)}
              style={{
                width: 250,
              }}
              value={doctor}
            />
            <Button type="primary" icon={<RedoOutlined />} size={14} onClick={clearFilters}>
              Clear filter
            </Button>
          </div>
        </div>
        <div className="relative overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 bg-gray-50">
              <tr>
                <th scope="col" className="px-3 py-3">
                  Full name
                </th>
                <th scope="col" className="px-3 py-3">
                  Email Address
                </th>
                <th scope="col" className="px-3 py-3">
                  Registered Date
                </th>
                <th scope="col" className="px-3 py-3">
                  Last Active Date
                </th>
                <th scope="col" className="px-3 py-3">
                  Account type
                </th>
                <th scope="col" className="px-3 py-3">
                  Subscription plan
                </th>
                <th scope="col" className="px-3 py-3">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Use doctorsData to populate rows */}
              {doctorsData?.users?.length > 0 ? (
                doctorsData?.users?.map((doctor, index) => (
                  <tr className="bg-white border-b" key={index}>
                    <th scope="row" className="px-3 py-4 font-medium text-gray-900">
                      <div className="flex items-center justify-start">
                        <img
                          src={
                            doctor?.profilePic
                              ? process.env.REACT_APP_BASE_IMG_URL + doctor?.profilePic
                              : "/images/user.png"
                          }
                          alt="profile"
                          className="w-8 h-8 rounded-full"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/images/user.png"; // Fallback image
                          }}
                        />
                        <span className="mx-2">
                          {doctor.firstName} {doctor.lastName}
                        </span>
                      </div>
                    </th>
                    <td className="px-3 py-4 vertical-top">{doctor.email}</td>
                    <td className="px-3 py-4 vertical-top">{moment(doctor.createdAt).format("DD/MM/YYYY")}</td>
                    <td className="px-3 py-4 vertical-top">{moment(doctor.updatedAt).format("DD/MM/YYYY")}</td>
                    <td className="px-3 py-4 vertical-top">{changeValue(doctor.accountType)}</td>
                    <td className="px-3 py-4 vertical-top capitalize">
                      {doctor?.subscription !== null
                        ? `${doctor?.subscription?.plan_type} ${
                            doctor?.subscription?.plan_type === "pro"
                              ? doctor?.subscription?.trial_cancel_date === null
                                ? "(Trial)"
                                : ""
                              : ""
                          }`
                        : "No subscription"}
                    </td>
                    <td className="px-3 py-4 vertical-top cursor-pointer ml-1">
                      <DeleteFilled
                        onClick={() => {
                          setConfirm(true);
                          setDocId(doctor.id);
                        }}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    No doctors found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between items-center mt-4">
          <div className="text-base font-semibold">
            Total Doctors - <span className="font-normal">{doctorsData.totalCount || 0}</span>
          </div>
          {/* <PaginationComponent page={page} total={doctorsData.totalCount} /> */}
        </div>
      </Card>
    </>
  );
};

export default ManageDoctor;
